
import React, { useEffect } from 'react';
import Layout from '@/components/Layout/Layout';
import LoanCalculator from '@/components/SpecialCalculators/LoanCalculator';
import { Link } from 'react-router-dom';

const LoanCalculatorPage = () => {
  // Scroll to top when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4">
        {/* Header Section */}
        <div className="mb-8">
          <div className="text-sm text-gray-500 mb-2">
            <Link to="/" className="hover:underline">Home</Link> {' > '} 
            <Link to="/special" className="hover:underline">Special Tools</Link> {' > '} 
            <span>Loan Calculator</span>
          </div>
          <h1 className="text-3xl font-bold mb-3">Loan Calculator</h1>
          <p className="text-gray-700">
            Calculate your loan payments, determine how much interest you'll pay, and understand the true cost of borrowing.
          </p>
        </div>

        {/* Calculator Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <LoanCalculator />
        </div>

        {/* Information Section */}
        <div className="bg-[#E6F3FF] p-6 rounded-lg mb-8">
          <h2 className="text-2xl font-semibold mb-3">How to use the Loan Calculator</h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-2">
            <li>Enter the loan amount you wish to borrow</li>
            <li>Enter the annual interest rate</li>
            <li>Enter the loan term in years</li>
            <li>Select your payment frequency (weekly, bi-weekly, or monthly)</li>
            <li>The calculator will automatically show your payment amount, total payments, and total interest</li>
          </ol>
        </div>

        {/* Additional Information */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">About Loan Calculations</h2>
          <p className="text-gray-700 mb-3">
            This loan calculator uses the standard amortization formula to calculate fixed payments over the life of your loan. 
            Each payment is applied first to the interest due, and then to the principal balance. Over time, you'll pay less 
            in interest and more toward the principal.
          </p>
          <p className="text-gray-700 mb-3">
            Changing the payment frequency can affect the total interest paid. More frequent payments (weekly or bi-weekly) 
            typically result in lower total interest costs compared to monthly payments, as you're reducing the principal more 
            frequently, which means less interest accrues.
          </p>
          <p className="text-gray-700 mb-3">
            Remember that this calculator provides estimates based on the information you enter. Actual loan terms and payments 
            may vary depending on factors such as your credit score, the lender's specific terms, and any additional fees or charges.
          </p>
        </div>

        {/* Related Calculators */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Related Calculators</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/special/mortgage" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">🏠</span> Mortgage Calculator
            </Link>
            <Link to="/special/bmi" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">⚖️</span> BMI Calculator
            </Link>
            <Link to="/special/tip" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">🍽️</span> Tip Calculator
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LoanCalculatorPage;
